import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Auth, Language } from '@barracuda/shared';
import { useAppContext } from '../../context/App.context';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useLocation } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { Box, Grid, Link, MenuItem, Select, Skeleton, Typography } from '@mui/material';
import { theme } from '@theme';
import {
  faqArticlesContainer,
  faqCategoriesContainer,
  faqCategoriesHeader,
  faqCategoriesMobileContainer,
  faqCategoryTitle,
  faqContactUsContainer,
  faqContactUsLink,
  faqHeader,
  faqHeaderParagraph,
  faqTitle,
  faqTabletCategoriesBox
} from './Faq.styles';
import { useTranslation, Trans } from 'react-i18next';
import { CategoryItem } from '../CategoryItem/CategoryItem';
import { QuestionItem } from '../QuestionItem/QuestionItem';

export interface FaqCategory {
  id: number;
  label: string;
}

export interface FaqQuestion {
  questionId: number;
  questionLabel: string;
  text: string;
  faqUrl: string;
}

const useQueryParams = (): { expandId: string } => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const expandId = searchParams.get('expandId') as string;
  return { expandId };
};

const handleContactUs = () => {
  window.open(process.env.REACT_APP_HELP_CENTER_LINK);
};

const languageMap: Record<Language, 'JAPANESE' | 'KOREAN' | 'ENGLISH'> = {
  JPN: 'JAPANESE',
  KOR: 'KOREAN',
  ENG: 'ENGLISH'
};

export const Faq: FC = () => {
  const { language } = useAppContext();
  const navigate = useLocalizedNavigate();
  const { expandId } = useQueryParams();
  const [activeCategoryId, setActiveCategoryId] = useState<number | undefined>(0);
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [questions, setQuestions] = useState<FaqQuestion[]>([]);
  const { t } = useTranslation();
  const faqLanguage = useMemo(() => languageMap[language || 'ENG'] || 'ENGLISH', [language]);

  useEffect(() => {
    Auth.sopClient.getFaqCategories(faqLanguage).then((result: AxiosResponse<FaqCategory[]>) => {
      setCategories(result.data);
      setActiveCategoryId(result.data?.[0]?.id);
    });
  }, []);

  useEffect(() => {
    if (!activeCategoryId) {
      return;
    }
    setQuestions([]);
    Auth.sopClient
      .getFaqQuestions(faqLanguage, activeCategoryId)
      .then((result: AxiosResponse<FaqQuestion[]>) => setQuestions(result.data));
  }, [activeCategoryId, language]);

  useEffect(() => {
    if (expandId) {
      scrollToElement();
    }
  });

  const currentCategoryLabel = useCallback(() => {
    return categories.find((category) => {
      return category.id === activeCategoryId;
    })?.label;
  }, [activeCategoryId]);

  const removeExpandId = () => {
    const searchParams = new URL(location.href).searchParams;
    searchParams.delete('expandId');
    navigate({ search: searchParams.toString() });
  };

  const scrollToElement = () => {
    const element = document.getElementById(expandId);
    if (element) {
      const topPosition = element.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({
        top: topPosition,
        behavior: 'smooth'
      });
    }
  };

  const onChangeCategory = useCallback((categoryId: number) => {
    setActiveCategoryId(categoryId);
    removeExpandId();
  }, []);

  const categoryItems = useMemo(() => {
    if (!categories.length) {
      return <Skeleton animation='wave' variant='rectangular' height={400} />;
    }
    return categories.map((category, index) => (
      <CategoryItem
        onClick={() => onChangeCategory(category.id)}
        active={activeCategoryId === category.id}
        label={category.label}
        key={`category-item-${index}`}
      />
    ));
  }, [categories, activeCategoryId]);

  const QuestionsSkeleton = useCallback(
    () => (
      <Box>
        {[1, 2, 3, 4].map((index) => (
          <Skeleton
            key={`question-skeleton-${index}`}
            sx={{ mb: '10px' }}
            animation='wave'
            variant='rectangular'
            height={68}
          />
        ))}
      </Box>
    ),
    []
  );

  return (
    <>
      <Box sx={{ background: theme.palette.common.lightGrey10 }}>
        <Grid container>
          <Grid item xs={12} sx={faqHeader}>
            <Typography sx={faqTitle} component='h1'>
              {t('faqPage.header')}
            </Typography>
            <Typography sx={faqHeaderParagraph}>
              {t('faqPage.headerText', {
                productName: process.env.REACT_APP_GENERAL_PRODUCT_NAME
              })}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Grid container sx={[faqHeader, { paddingTop: '40px' }]}>
        <Grid item xs={12} sx={faqCategoriesMobileContainer}>
          <Typography>{t('faqPage.categories')}</Typography>
          <Select
            value={activeCategoryId}
            onChange={(event) => onChangeCategory(Number(event.target.value))}
          >
            {categories.map((category, index) => (
              <MenuItem key={'selectCategory' + index} value={category.id}>
                {category.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={3} sx={[faqCategoriesContainer, faqTabletCategoriesBox]}>
          <Typography sx={faqCategoriesHeader}>{t('faqPage.categories')}</Typography>
          {categoryItems}
          <Box sx={faqContactUsContainer}>
            <Trans
              i18nKey='faqPage.sidebarText'
              components={{
                a: <Link onClick={handleContactUs} sx={faqContactUsLink} component='span' />
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={8} sx={[{ paddingLeft: '80px' }, faqArticlesContainer]}>
          <Typography sx={faqCategoryTitle} component='h2'>
            {currentCategoryLabel()}
          </Typography>
          {!questions.length && <QuestionsSkeleton />}
          {questions.map((question, index) => (
            <QuestionItem
              question={question}
              onClick={removeExpandId}
              key={`question-item-${index}`}
              expanded={question.faqUrl === expandId}
              categoryId={activeCategoryId || 0}
              index={index}
            />
          ))}
        </Grid>
      </Grid>
    </>
  );
};
